// src/pages/inventory/Pharmacy/MedicineRequestsList.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  TextField,
  Stack,
  Grid,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  styled,
  tableCellClasses,
} from "@mui/material";
import {
  getAllMedicineRequests,
  getRequestsByStatus,
  approveMedicineRequest,
  rejectMedicineRequest,
  issueMedicineRequest,
} from "../../../services/medicineRequestService";
import { createInvoice } from "../../../services/invoiceService";
import { uploadAttachment } from "../../../services/uploadService";
import { getCostPerUnitById } from "../../../services/medicineNewService";
import { addToPatientInventory } from "../../../services/patientMedicineInventoryService";
import { getMedicineInventoryList } from "../../../services/medicineInventoryService";
import { getAssignmentByAdmissionId } from "../../../services/PatientAssignmentService";
import { getDoctorById } from "../../../services/doctorService";
import { useAuth } from "../../../context/AuthContext";
import { getOrganizationName, getOrganizationId } from "../../../utils/organizationUtils";
import { filterByOrganization } from "../../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import dayjs from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { usePharmacyExpiryThreshold } from "../../../hooks/usePharmacyExpiryThreshold";
import { useOrganizationGstPercentage } from "../../../hooks/useOrganizationGstPercentage";
import { triggerNearExpiryLowStockAlert } from "../../../services/pharmacySettingsService";
import { formatExpiryThresholdLabel } from "../../../utils/medicineExpiryThreshold";
import PaymentModeSelector from "../../../components/PaymentModeSelector";
import {
  InvoiceAttachmentControl,
  InvoiceRemarksField,
  calcInvoiceGstBreakdown,
  resolveAttachmentUrl,
} from "../../../components/InvoiceFormShared";
import {
  pickInventorySupplierFields,
  resolveSupplierForIssueInvoice,
} from "../../../utils/medicineIssueSupplierUtils";
import { resolveInvoiceDepartmentId } from "../../../utils/invoiceRelationUtils";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from "../../../utils/pageLayoutStyles";

// Utility function to safely format dates
const formatDate = (dateValue, format = "DD MMM YYYY") => {
  if (!dateValue) return "-";
  
  let date;
  
  // Handle array format [year, month, day, hour, minute, second]
  if (Array.isArray(dateValue) && dateValue.length >= 6) {
    // Convert array to Date object (month is 0-indexed in JavaScript Date)
    date = dayjs(new Date(dateValue[0], dateValue[1] - 1, dateValue[2], dateValue[3], dateValue[4], dateValue[5]));
  } else {
    // Handle string/Date format
    date = dayjs(dateValue);
  }
  
  return date.isValid() ? date.format(format) : "Invalid Date";
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#568af7',
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
}));

const MedicineRequestsList = () => {
  const { user } = useAuth();
  const { isNearExpiry, months: expiryThresholdMonths } = usePharmacyExpiryThreshold();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterPatient, setFilterPatient] = useState("");
  const [filterMedicine, setFilterMedicine] = useState("");
  
  // Dialog states
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openIssueDialog, setOpenIssueDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [paymentMode, setPaymentMode] = useState({ mode: "cash" });
  const [particulars, setParticulars] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [batchInfo, setBatchInfo] = useState({
    batchNumber: "N/A",
    expiryDate: null,
    isExpired: false,
    isNearExpiry: false,
    availableQuantity: 0,
    supplierId: null,
    supplierName: null,
    stockInvoiceId: null,
  });
  const gstPercentage = useOrganizationGstPercentage(user, {
    enabled: openIssueDialog,
  });

  const statusTabs = [
    { label: "Pending Requests", value: "REQUESTED", color: "warning" },
    { label: "Approved", value: "APPROVED", color: "info" },
    { label: "Issued", value: "ISSUED", color: "success" },
    { label: "Received", value: "RECEIVED", color: "primary" },
    { label: "Dispensed", value: "DISPENSED", color: "default" },
    { label: "Returned", value: "RETURNED", color: "error" },
    { label: "Rejected", value: "REJECTED", color: "error" },
  ];

  useEffect(() => {
    fetchRequests();
  }, [activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const status = statusTabs[activeTab].value;
      const data = await getRequestsByStatus(status);
      // Filter by organization as defensive measure (backend should already filter via organizationId query param)
      const filteredData = filterByOrganization(data || [], user);
      setRequests(filteredData);
      setFilteredRequests(filteredData);
    } catch (error) {
      console.error("Error fetching medicine requests:", error);
      setRequests([]);
      setFilteredRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter requests based on search and filters
  useEffect(() => {
    let filtered = requests;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const vals = [
          r.id,
          r.patientId,
          r.medicineName,
          r.requestedByName,
          r.approvedByName,
        ].filter(Boolean).map(v => String(v).toLowerCase());
        return vals.some(v => v.includes(q));
      });
    }

    if (filterPatient.trim()) {
      filtered = filtered.filter(r =>
        String(r.patientId || "").includes(filterPatient)
      );
    }

    if (filterMedicine.trim()) {
      const q = filterMedicine.toLowerCase();
      filtered = filtered.filter(r =>
        String(r.medicineName || "").toLowerCase().includes(q)
      );
    }

    setFilteredRequests(filtered);
    setPage(0);
  }, [searchQuery, filterPatient, filterMedicine, requests]);

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      const approvalData = {
        approvedByUserId: user?.id || 0,
        approvedByName: user?.name || user?.username || "Unknown",
        approvedDate: new Date().toISOString(),
      };

      await approveMedicineRequest(selectedRequest.id, approvalData);
      
      setOpenApproveDialog(false);
      setSelectedRequest(null);
      await fetchRequests();
      
    } catch (error) {
      console.error("Error approving request:", error);
      alert("Failed to approve request. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    setProcessing(true);
    try {
      const rejectionData = {
        approvedByUserId: user?.id || 0,
        approvedByName: user?.name || user?.username || "Unknown",
        rejectionReason: rejectionReason,
        approvedDate: new Date().toISOString(),
      };

      await rejectMedicineRequest(selectedRequest.id, rejectionData);
      
      setOpenRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason("");
      await fetchRequests();
      
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert("Failed to reject request. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  // Fetch batch info with FEFO logic when issue dialog opens
  const fetchBatchInfo = async (medicineId) => {
    try {
      // Fetch inventory from organization-specific medicine_inventory table
      // getMedicineInventoryList uses apiClient which automatically adds organizationId
      // This ensures we only work with organization-specific inventory, not base medicine_raw_details
      const inventoryList = await getMedicineInventoryList();
      
      if (inventoryList && inventoryList.length > 0) {
        // Filter by organization as defensive measure (backend should already filter via organizationId)
        // This ensures we only use organization-specific medicine_inventory, not base medicine_raw_details
        const filteredInventory = filterByOrganization(inventoryList, user);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Filter for this medicine with available quantity (include expired for display)
        // This filters from organization-specific medicine_inventory table
        const medicineInventory = filteredInventory.filter(item => {
          return item.medicineId === medicineId && item.quantity > 0;
        });

        if (medicineInventory.length > 0) {
          // Prepare arrays for non-expired and expired with parsed dates
          const mapped = medicineInventory.map((item) => {
            const raw = item.expiryDate || item.expiry_date || item.expirydate;
            const d = raw ? new Date(raw) : null;
            if (d) d.setHours(0, 0, 0, 0);
            return { item, expiry: d };
          });

          const nonExpired = mapped.filter(x => x.expiry && x.expiry > today);
          const expired = mapped.filter(x => x.expiry && x.expiry <= today);

          let chosen = null;
          let isExpired = false;

          if (nonExpired.length > 0) {
            // FEFO: pick earliest non-expired
            nonExpired.sort((a, b) => a.expiry - b.expiry);
            chosen = nonExpired[0];
            isExpired = false;
          } else if (expired.length > 0) {
            // If only expired, pick the most recently expired (latest expiry)
            expired.sort((a, b) => b.expiry - a.expiry);
            chosen = expired[0];
            isExpired = true;
          } else {
            // No usable expiry dates
            chosen = { item: medicineInventory[0], expiry: null };
            isExpired = false;
          }

          const selectedBatch = chosen.item;
          const expiryDateValue = chosen.expiry ? chosen.expiry.toISOString() : (selectedBatch.expiryDate || selectedBatch.expiry_date || selectedBatch.expirydate || null);
          const supplierFields = pickInventorySupplierFields(selectedBatch);

          setBatchInfo({
            batchNumber: selectedBatch.batchNumber || selectedBatch.batch_number || "N/A",
            expiryDate: expiryDateValue,
            isExpired: isExpired,
            isNearExpiry: !isExpired && isNearExpiry(expiryDateValue),
            availableQuantity: selectedBatch.quantity || 0,
            supplierId: supplierFields.supplierId,
            supplierName: supplierFields.supplierName,
            stockInvoiceId: supplierFields.stockInvoiceId,
          });
        } else {
          setBatchInfo({
            batchNumber: "N/A",
            expiryDate: null,
            isExpired: false,
            isNearExpiry: false,
            availableQuantity: 0,
            supplierId: null,
            supplierName: null,
            stockInvoiceId: null,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching batch info:", error);
      setBatchInfo({
        batchNumber: "N/A",
        expiryDate: null,
        isExpired: false,
        isNearExpiry: false,
        availableQuantity: 0,
        supplierId: null,
        supplierName: null,
        stockInvoiceId: null,
      });
    }
  };

  const handleIssue = async () => {
    if (!selectedRequest) return;

    // Check for expired medicine before proceeding
    if (batchInfo.isExpired) {
      alert("Warning: This medicine has expired. Please do not issue expired medicines to patients. Consider returning it to pharmacy or disposing it properly.");
      return;
    }

    // Check for insufficient quantity
    if (batchInfo.availableQuantity < selectedRequest.totalQuantity) {
      alert(`Warning: Insufficient quantity available. Available: ${batchInfo.availableQuantity} units, Required: ${selectedRequest.totalQuantity} units. Please add more stock before issuing.`);
      return;
    }

    if (paymentMode?.mode === "credit" && !paymentMode?.organizationId) {
      alert("Please select an organization for credit payment.");
      return;
    }

    setProcessing(true);
    try {
      // Fetch cost per unit
      const costPerUnit = await getCostPerUnitById(selectedRequest.medicineId);
      if (!costPerUnit) {
        throw new Error("Cost per unit not found for this medicine");
      }

      const totalCost = selectedRequest.totalQuantity * costPerUnit;
      const {
        sgst,
        cgst,
        totalAmount: totalInvoiceAmount,
      } = calcInvoiceGstBreakdown(totalCost, gstPercentage);

      const hospitalOrgId = getOrganizationId(user);
      const hospitalFromName = getOrganizationName(user) || "Aryan Hospital";
      const paymentModeValue = (paymentMode?.mode || "cash").toUpperCase();
      const creditOrganizationId =
        paymentModeValue === "CREDIT"
          ? paymentMode?.organizationId || null
          : null;

      let attachmentUrl = null;
      if (attachmentFile instanceof File) {
        const uploaded = await uploadAttachment(attachmentFile);
        attachmentUrl = resolveAttachmentUrl(uploaded);
      }

      // Copy supplier from stock purchase invoice (via inventory batch) so
      // transaction history can resolve supplier name from this issue invoice.
      const supplierInfo = await resolveSupplierForIssueInvoice(batchInfo);

      // Resolve doctor / admission before invoice create so FKs persist on invoice row
      let resolvedDoctorId =
        selectedRequest?.doctorId ||
        selectedRequest?.doctor_id ||
        null;
      let resolvedDepartmentId =
        selectedRequest?.departmentId ||
        selectedRequest?.department_id ||
        null;
      if (!resolvedDoctorId && selectedRequest.admissionId) {
        try {
          const assignments = await getAssignmentByAdmissionId(
            selectedRequest.admissionId
          );
          const assignment = Array.isArray(assignments)
            ? assignments[0]
            : assignments;
          resolvedDoctorId = assignment?.doctorId || assignment?.doctor_id || null;
          resolvedDepartmentId =
            resolvedDepartmentId ||
            assignment?.departmentId ||
            assignment?.department_id ||
            null;
        } catch {
          // Non-blocking — invoice can still be created without doctor_id
        }
      }

      if (!resolvedDepartmentId) {
        resolvedDepartmentId = await resolveInvoiceDepartmentId({
          departmentId: resolvedDepartmentId,
          doctorId: resolvedDoctorId,
          admissionId: selectedRequest.admissionId,
        });
      }

      // Create invoice
      const invoice = await createInvoice({
        amount: totalCost,
        discount: 0,
        sgst: sgst,
        cgst: cgst,
        totalAmount: totalInvoiceAmount,
        date: new Date().toISOString(),
        particulars: particulars?.trim() || "IPD MEDICINE REQUEST",
        invoiceType: "MEDICINE",
        patientId: selectedRequest.patientId,
        doctorId:
          resolvedDoctorId != null && resolvedDoctorId !== ""
            ? Number(resolvedDoctorId)
            : undefined,
        admissionId:
          selectedRequest.admissionId != null && selectedRequest.admissionId !== ""
            ? Number(selectedRequest.admissionId)
            : undefined,
        prescriptionId:
          selectedRequest.prescriptionId ||
          selectedRequest.prescription_id ||
          selectedRequest.ipdPrescriptionId ||
          selectedRequest.ipd_prescription_id
            ? Number(
                selectedRequest.prescriptionId ||
                  selectedRequest.prescription_id ||
                  selectedRequest.ipdPrescriptionId ||
                  selectedRequest.ipd_prescription_id
              )
            : undefined,
        departmentId: resolvedDepartmentId,
        fromName: hospitalFromName,
        toName: selectedRequest.patientName || null,
        organizationId: hospitalOrgId || undefined,
        paymentMode: paymentModeValue,
        creditOrganizationId,
        supplierId: supplierInfo.supplierId || null,
        ...(supplierInfo.supplierName
          ? { supplierName: supplierInfo.supplierName }
          : {}),
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      });

      // Fetch doctorName if not available in request (for IPD requests)
      // Backend needs doctorName for transaction record in {orgId}_medicine_transactions
      let doctorName = selectedRequest?.doctorName || null;
      if (!doctorName && selectedRequest.admissionId) {
        try {
          // Fetch assignment to get doctorId
          const assignments = await getAssignmentByAdmissionId(selectedRequest.admissionId);
          const assignment = Array.isArray(assignments) ? assignments[0] : assignments;
          
          if (assignment?.doctorId) {
            // Fetch doctor name using doctorId
            const doctor = await getDoctorById(assignment.doctorId);
            if (doctor?.name) {
              doctorName = doctor.name;
            }
          }
        } catch (error) {
          console.warn("Failed to fetch doctor name for transaction:", error);
          // Continue without doctorName - backend will handle gracefully
        }
      }

      // Mark request as issued
      // Backend will handle inventory deduction from organization-specific medicine_inventory table
      // NOT from base medicine_raw_details table (which is only for reference data)
      // Include batch info so backend knows which specific inventory record to reduce from
      // Include patientId and admissionId for transaction creation in {orgId}_medicine_transactions
      // Backend will fetch patientName from organization-specific patient table
      const issueData = {
        issuedByUserId: user?.id || 0,
        issuedByName: user?.name || user?.username || "Unknown",
        issuedDate: new Date().toISOString(),
        invoiceId: invoice.id,
        prescriptionId:
          selectedRequest.prescriptionId ||
          selectedRequest.prescription_id ||
          selectedRequest.ipdPrescriptionId ||
          selectedRequest.ipd_prescription_id ||
          invoice?.prescriptionId ||
          invoice?.prescription_id ||
          null,
        doctorName: doctorName, // ✅ Fetched from assignment if not in request
        // Include batch information for inventory reduction from organization-specific medicine_inventory
        batchNumber: batchInfo.batchNumber !== "N/A" ? batchInfo.batchNumber : null,
        expiryDate: batchInfo.expiryDate || null,
        // Include medicineId to ensure backend uses organization-specific medicine_inventory table
        medicineId: selectedRequest.medicineId,
        // Include patientId and admissionId for transaction creation in {orgId}_medicine_transactions
        patientId: selectedRequest.patientId || null,
        admissionId: selectedRequest.admissionId || null,
        // Include quantity for transaction record
        quantity: selectedRequest.totalQuantity || 0,
        // Include patient type (IPD for IPD requests)
        patientType: selectedRequest.admissionId ? "ipd" : "opd",
        // Include transaction type for transaction record
        transactionType: "OUT", // Medicine issued = OUT transaction
        // Include medicine name for transaction record
        medicineName: selectedRequest.medicineName || null,
        // Include dosage for transaction record
        dosage: selectedRequest.dosage || null,
      };

      // Debug: Log what we're sending to backend
      // Verify organization-specific inventory operations
      const storedUser = localStorage.getItem("user");
      let organizationId = null;
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          organizationId = parsedUser?.organizationId || null;
        } catch (e) {
          console.error("Error parsing user:", e);
        }
      }
      
      console.log("Issuing medicine - Organization-specific inventory operation:", {
        requestId: selectedRequest.id,
        organizationId: organizationId || "Base table (common user)",
        issueData: issueData,
        batchInfo: batchInfo,
        note: "Inventory reduction will happen in organization-specific medicine_inventory table, not base medicine_raw_details"
      });

      // Call the backend issue method
      // apiClient will automatically add organizationId as query parameter
      // Backend should reduce quantity from organization-specific medicine_inventory table
      const issueResponse = await issueMedicineRequest(selectedRequest.id, issueData);

      if (batchInfo.expiryDate && batchInfo.isNearExpiry) {
        await triggerNearExpiryLowStockAlert({
          medicineId: selectedRequest.medicineId,
          medicineName: selectedRequest.medicineName,
          batchNumber: batchInfo.batchNumber,
          expiryDate: batchInfo.expiryDate,
          quantity: selectedRequest.totalQuantity,
          source: "IPD_REQUEST_ISSUE",
        });
      }
      
      // Debug: Log what backend returned
      console.log("Backend response:", issueResponse);
      
      // Use the inventory information from the backend response
      if (issueResponse && issueResponse.inventoryInfo) {
        const inventoryData = {
          patientId: issueResponse.inventoryInfo.patientId,
          admissionId: issueResponse.inventoryInfo.admissionId,
          medicineId: issueResponse.inventoryInfo.medicineId,
          medicineName: issueResponse.inventoryInfo.medicineName,
          dosage: issueResponse.inventoryInfo.dosage,
          quantity: issueResponse.inventoryInfo.totalQuantity,
          batchNumber: issueResponse.inventoryInfo.batchNumber, // ✅ Now comes from backend
          expiryDate: issueResponse.inventoryInfo.expiryDate,   // ✅ Now comes from backend
          issuedDate: new Date().toISOString(),
          issuedBy: user?.name || user?.username || "Unknown",
        };
        
        await addToPatientInventory(inventoryData);
      }
      
      const wasNearExpiry = batchInfo.isNearExpiry;

      setOpenIssueDialog(false);
      setSelectedRequest(null);
      setPaymentMode({ mode: "cash" });
      setParticulars("");
      setAttachmentFile(null);
      setAttachmentName("");
      setBatchInfo({
        batchNumber: "N/A",
        expiryDate: null,
        isExpired: false,
        isNearExpiry: false,
        availableQuantity: 0,
        supplierId: null,
        supplierName: null,
        stockInvoiceId: null,
      });
      await fetchRequests();
      
      if (wasNearExpiry) {
        alert(
          `Medicine issued. Batch expires within ${formatExpiryThresholdLabel(expiryThresholdMonths)} — low-stock / reorder alert sent if configured.`
        );
      } else {
        alert("Medicine issued successfully! Inventory has been updated.");
      }
      
    } catch (error) {
      console.error("Error issuing medicine:", error);
      alert(`Failed to issue medicine: ${error.message || "Unknown error"}`);
    } finally {
      setProcessing(false);
    }
  };


  const getStatusChip = (status) => {
    const statusConfig = {
      REQUESTED: { color: "warning", label: "Pending" },
      APPROVED: { color: "info", label: "Approved" },
      REJECTED: { color: "error", label: "Rejected" },
      ISSUED: { color: "success", label: "Issued" },
      RECEIVED: { color: "primary", label: "Received" },
      DISPENSED: { color: "default", label: "Dispensed" },
      RETURNED: { color: "error", label: "Returned" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleExport = async () => {
    const rows = filteredRequests || [];
    const isReturnedTab = statusTabs[activeTab].value === "RETURNED";
    const headers = [
      "Request ID",
      "Patient ID",
      "Medicine",
      "Quantity",
      ...(isReturnedTab ? ["Returned Quantity"] : []),
      "Requested By",
      "Requested Date",
      "Status",
      "Approved By",
      "Issued Date",
    ];
    const tableRows = rows.map((r) => [
      r.id,
      r.patientId,
      r.medicineName,
      r.totalQuantity,
      ...(isReturnedTab ? [r.returnedQuantity || 0] : []),
      r.requestedByName,
      formatDate(r.requestedDate, "YYYY-MM-DD"),
      r.status,
      r.approvedByName || "",
      formatDate(r.issuedDate, "YYYY-MM-DD"),
    ]);

    await downloadStyledTableExcel({
      filename: `medicine_requests_${statusTabs[activeTab].value}_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Medicine Requests",
      title: "Medicine Requests",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterPatient("");
    setFilterMedicine("");
  };

  return (
    <Box>
      {/* Tabs for status filtering */}
      <Box sx={{ ...PAGE_TABS_BAR_SX, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          {...PAGE_TAB_LIST_PROPS}
        >
          {statusTabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Search and Filter Bar */}
      <Box sx={{ mb: 1, p: 1.5, pt: 1, pb: 1 }}>
        <Box sx={TABLE_SEARCH_ROW_SX}>
          <TextField
            placeholder="Search requests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={fetchRequests}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} sx={{ mt: 1 }}>
            <TextField
              label="Patient ID"
              value={filterPatient}
              onChange={(e) => setFilterPatient(e.target.value)}
              size="small"
            />
            <TextField
              label="Medicine Name"
              value={filterMedicine}
              onChange={(e) => setFilterMedicine(e.target.value)}
              size="small"
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>

      {/* Requests Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>Request ID</StyledTableCell>
              <StyledTableCell>Patient ID</StyledTableCell>
              <StyledTableCell>Medicine</StyledTableCell>
              <StyledTableCell>Dosage</StyledTableCell>
              <StyledTableCell>Duration</StyledTableCell>
              <StyledTableCell>Quantity</StyledTableCell>
              {statusTabs[activeTab].value === "RETURNED" && (
                <StyledTableCell>Returned Quantity</StyledTableCell>
              )}
              <StyledTableCell>Requested By</StyledTableCell>
              <StyledTableCell>Requested Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={statusTabs[activeTab].value === "RETURNED" ? 11 : 10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={statusTabs[activeTab].value === "RETURNED" ? 11 : 10} align="center">
                  <Alert severity="info">No medicine requests found for this status</Alert>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>{request.id}</TableCell>
                    <TableCell>{request.patientId}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {request.medicineName}
                      </Typography>
                    </TableCell>
                    <TableCell>{request.dosage}</TableCell>
                    <TableCell>{request.duration} days</TableCell>
                    <TableCell>{request.totalQuantity} units</TableCell>
                    {statusTabs[activeTab].value === "RETURNED" && (
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="error">
                          {request.returnedQuantity || 0} units
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>{request.requestedByName}</TableCell>
                    <TableCell>
                      {formatDate(request.requestedDate)}
                    </TableCell>
                    <TableCell>{getStatusChip(request.status)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {/* View Details */}
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              setSelectedRequest(request);
                              setOpenViewDialog(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Approve/Reject for REQUESTED status */}
                        {request.status === "REQUESTED" && (
                          <>
                            <Tooltip title="Approve Request">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setOpenApproveDialog(true);
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setOpenRejectDialog(true);
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {/* Issue Medicine for APPROVED status */}
                        {request.status === "APPROVED" && (
                          <Tooltip title="Issue Medicine">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedRequest(request);
                                setPaymentMode({ mode: "cash" });
                                setParticulars("");
                                setAttachmentFile(null);
                                setAttachmentName("");
                                setOpenIssueDialog(true);
                              }}
                            >
                              <LocalShippingIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredRequests.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* Approve Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={() => !processing && setOpenApproveDialog(false)}
        maxWidth="md"
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Approve Medicine Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to approve this medicine request?
              </Typography>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Request ID:</strong> {selectedRequest.id}</Typography>
                <Typography variant="body2"><strong>Patient ID:</strong> {selectedRequest.patientId}</Typography>
                <Typography variant="body2"><strong>Medicine:</strong> {selectedRequest.medicineName}</Typography>
                <Typography variant="body2"><strong>Quantity:</strong> {selectedRequest.totalQuantity} units</Typography>
                <Typography variant="body2"><strong>Requested By:</strong> {selectedRequest.requestedByName}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setOpenApproveDialog(false)} disabled={processing} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button onClick={handleApprove} variant="contained" color="primary" sx={FORM_SUBMIT_BUTTON_SX} disabled={processing}>
            {processing ? <CircularProgress size={20} /> : "Approve Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={() => !processing && setOpenRejectDialog(false)}
        maxWidth="md"
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Reject Medicine Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Please provide a reason for rejecting this request:
              </Typography>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Request ID:</strong> {selectedRequest.id}</Typography>
                <Typography variant="body2"><strong>Medicine:</strong> {selectedRequest.medicineName}</Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Rejection Reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
                disabled={processing}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={processing} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="primary"
            sx={FORM_SUBMIT_BUTTON_SX}
            disabled={processing || !rejectionReason.trim()}
          >
            {processing ? <CircularProgress size={20} /> : "Reject Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue Medicine Dialog */}
      <Dialog 
        open={openIssueDialog} 
        onClose={() => !processing && setOpenIssueDialog(false)}
        maxWidth="md"
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
        onTransitionEnter={() => {
          if (selectedRequest) {
            fetchBatchInfo(selectedRequest.medicineId);
          }
        }}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
          Issue Medicine to Ward
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              {/* Warning for expired medicine */}
              {batchInfo.isExpired && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <strong>Warning:</strong> This medicine has expired! Please do not issue expired medicines to patients. Consider returning it to pharmacy or disposing it properly.
                </Alert>
              )}

              {!batchInfo.isExpired && batchInfo.isNearExpiry && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <strong>Near expiry:</strong> This batch expires within{" "}
                  {formatExpiryThresholdLabel(expiryThresholdMonths)}. Issuing will use FEFO
                  stock and may trigger a low-stock / reorder alert.
                </Alert>
              )}

              {/* Warning for insufficient quantity */}
              {batchInfo.availableQuantity < selectedRequest.totalQuantity && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <strong>Warning:</strong> Insufficient quantity available. Available: {batchInfo.availableQuantity} units, Required: {selectedRequest.totalQuantity} units. Please add more stock before issuing.
                </Alert>
              )}

              {/* Medicine Details Table */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                Medicine Details
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Medicine Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Batch Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Dosage</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{selectedRequest.medicineName}</TableCell>
                      <TableCell>{batchInfo.batchNumber}</TableCell>
                      <TableCell>
                        {batchInfo.expiryDate 
                          ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span style={{ color: batchInfo.isExpired ? 'red' : 'inherit' }}>
                                {dayjs(batchInfo.expiryDate).format("DD MMM YYYY")}
                              </span>
                              {batchInfo.isExpired && (
                                <Chip label="EXPIRED" color="error" size="small" />
                              )}
                            </Box>
                          )
                          : "N/A"}
                      </TableCell>
                      <TableCell>{selectedRequest.dosage || "-"}</TableCell>
                      <TableCell>{selectedRequest.duration} days</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {selectedRequest.totalQuantity} units
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Request Information */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                Request Information
              </Typography>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Request ID:</strong> #{selectedRequest.id}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Patient ID:</strong> {selectedRequest.patientId}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Requested By:</strong> {selectedRequest.requestedByName}
                </Typography>
                <Typography variant="body2">
                  <strong>Requested Date:</strong> {formatDate(selectedRequest.requestedDate, "DD MMM YYYY HH:mm")}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: "bold", color: "primary.main" }}>
                  Invoice / Payment
                </Typography>
                <Stack spacing={1.5}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <PaymentModeSelector value={paymentMode} onChange={setPaymentMode} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InvoiceAttachmentControl
                        attachmentName={attachmentName}
                        attachmentFile={attachmentFile}
                        onFileChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setAttachmentFile(file);
                          setAttachmentName(file.name);
                        }}
                        onDelete={() => {
                          setAttachmentFile(null);
                          setAttachmentName("");
                        }}
                      />
                    </Grid>
                  </Grid>
                  <InvoiceRemarksField
                    name="particulars"
                    value={particulars}
                    onChange={(e) => setParticulars(e.target.value)}
                  />
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button 
            onClick={() => {
              setOpenIssueDialog(false);
              setPaymentMode({ mode: "cash" });
              setParticulars("");
              setAttachmentFile(null);
              setAttachmentName("");
              setBatchInfo({
                batchNumber: "N/A",
                expiryDate: null,
                isExpired: false,
                isNearExpiry: false,
                availableQuantity: 0,
                supplierId: null,
                supplierName: null,
                stockInvoiceId: null,
              });
            }} 
            disabled={processing}
            variant="outlined"
            color="primary"
            sx={FORM_CANCEL_BUTTON_SX}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleIssue} 
            variant="contained" 
            color="primary"
            sx={FORM_SUBMIT_BUTTON_SX}
            disabled={
              processing ||
              !selectedRequest ||
              batchInfo.isExpired ||
              batchInfo.availableQuantity < (selectedRequest?.totalQuantity || 0) ||
              (paymentMode?.mode === "credit" && !paymentMode?.organizationId)
            }
          >
            {processing ? "Processing..." : "Issue Medicine"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth sx={STANDARD_FORM_DIALOG_PAPER_SX}>
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Medicine Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Request Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Request ID</Typography>
                    <Typography variant="body1">{selectedRequest.id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedRequest.status)}</Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Patient ID</Typography>
                    <Typography variant="body1">{selectedRequest.patientId}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Admission ID</Typography>
                    <Typography variant="body1">{selectedRequest.admissionId || '-'}</Typography>
                  </Box>
                  {selectedRequest.status === "RETURNED" && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Returned Quantity</Typography>
                      <Typography variant="body1" fontWeight="bold" color="error">
                        {selectedRequest.returnedQuantity || 0} units
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Medicine Details</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Medicine Name</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedRequest.medicineName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Dosage</Typography>
                    <Typography variant="body1">{selectedRequest.dosage}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Frequency</Typography>
                    <Typography variant="body1">{selectedRequest.frequency}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">{selectedRequest.duration} days</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Quantity</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedRequest.totalQuantity} units</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Workflow Timeline</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Requested */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label="Requested" color="warning" size="small" />
                    <Box>
                      <Typography variant="body2">{selectedRequest.requestedByName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                       {formatDate(selectedRequest.requestedDate, "DD MMM YYYY hh:mm A")}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Approved */}
                  {selectedRequest.approvedDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={selectedRequest.status === "REJECTED" ? "Rejected" : "Approved"}
                        color={selectedRequest.status === "REJECTED" ? "error" : "success"}
                        size="small"
                      />
                      <Box>
                        <Typography variant="body2">{selectedRequest.approvedByName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(selectedRequest.approvedDate, "DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Issued */}
                  {selectedRequest.issuedDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip label="Issued" color="primary" size="small" />
                      <Box>
                        <Typography variant="body2">{selectedRequest.issuedByName || 'Pharmacy'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(selectedRequest.issuedDate, "DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Received */}
                  {selectedRequest.receivedDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip label="Received" color="info" size="small" />
                      <Box>
                        <Typography variant="body2">{selectedRequest.receivedByName || 'Staff'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                        {formatDate(selectedRequest.requestedDate, "DD MMM YYYY hh:mm A")}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Dispensed */}
                  {selectedRequest.dispensedDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip label="Dispensed" color="success" size="small" />
                      <Box>
                        <Typography variant="body2">{selectedRequest.dispensedByName || 'Staff'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(selectedRequest.dispensedDate, "DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Returned */}
                  {selectedRequest.returnedDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip label="Returned" color="error" size="small" />
                      <Box>
                        <Typography variant="body2">{selectedRequest.returnedByName || 'Staff'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(selectedRequest.returnedDate, "DD MMM YYYY HH:mm")}
                        </Typography>
                        {selectedRequest.returnReason && (
                          <Typography variant="caption" display="block" color="error">
                            Reason: {selectedRequest.returnReason}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Remarks */}
              {selectedRequest.remarks && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Remarks</Typography>
                  <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                    <Typography variant="body2">{selectedRequest.remarks}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setOpenViewDialog(false)} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MedicineRequestsList;

